import { useState, useEffect, useCallback } from 'react';
import { Contract, formatUnits, JsonRpcProvider } from 'ethers';
import { TOKENS, ERC20_ABI, TERNOA_CHAIN } from '@/config/contracts';

export interface Balances {
  vbUsdc: number;
  vbUsdt: number;
  vbEth: number;
  caps: number;
  kat: number;
}

// Direct RPC provider for reading Ternoa balances — works regardless of wallet chain
const ternoaProvider = new JsonRpcProvider(TERNOA_CHAIN.rpcUrl);

export function useBalances(address: string | null) {
  const [balances, setBalances] = useState<Balances>({
    vbUsdc: 0,
    vbUsdt: 0,
    vbEth: 0,
    caps: 0,
    kat: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchBalances = useCallback(async () => {
    if (!address) return;

    setLoading(true);
    try {
      const [vbUsdcRaw, vbUsdtRaw, vbEthRaw, capsRaw, katRaw] = await Promise.all([
        // vbUSDC (ERC-20)
        new Contract(TOKENS.vbUSDC.address, ERC20_ABI, ternoaProvider)
          .balanceOf(address)
          .catch(() => 0n),
        // vbUSDT (ERC-20)
        new Contract(TOKENS.vbUSDT.address, ERC20_ABI, ternoaProvider)
          .balanceOf(address)
          .catch(() => 0n),
        // vbETH (ERC-20)
        new Contract(TOKENS.vbETH.address, ERC20_ABI, ternoaProvider)
          .balanceOf(address)
          .catch(() => 0n),
        // CAPS (native)
        ternoaProvider.getBalance(address).catch(() => 0n),
        // KAT (ERC-20)
        new Contract(TOKENS.KAT.address!, ERC20_ABI, ternoaProvider)
          .balanceOf(address)
          .catch(() => 0n),
      ]);

      setBalances({
        vbUsdc: parseFloat(formatUnits(vbUsdcRaw, TOKENS.vbUSDC.decimals)),
        vbUsdt: parseFloat(formatUnits(vbUsdtRaw, TOKENS.vbUSDT.decimals)),
        vbEth: parseFloat(formatUnits(vbEthRaw, TOKENS.vbETH.decimals)),
        caps: parseFloat(formatUnits(capsRaw, TOKENS.CAPS.decimals)),
        kat: parseFloat(formatUnits(katRaw, TOKENS.KAT.decimals)),
      });
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    fetchBalances();
    // Refresh every 30s
    const interval = setInterval(fetchBalances, 30_000);
    return () => clearInterval(interval);
  }, [fetchBalances]);

  return { balances, loading, refresh: fetchBalances };
}
